"use client";

import { useEffect, type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils/cn";

/**
 * Bottom-sheet modal — used for "Submit chore", "Approve", "New goal" etc.
 *
 * Headless: pass `open` + `onClose`. Keyboard ESC closes.
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  className,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Body-scroll lock — bez tego content pod modalem skrolluje się gdy
  // user gestem swipe poza panelem. Zapamiętujemy poprzednią wartość
  // żeby nie zniszczyć inline overflow ustawionego gdzieś indziej.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" role="dialog" aria-modal="true">
      <button
        aria-label="Zamknij"
        className="absolute inset-0 bg-black/40 animate-in fade-in duration-150"
        onClick={onClose}
      />
      <div
        className={cn(
          "relative w-full max-w-md bg-card text-ink rounded-t-[24px] border-t border-x border-line shadow-2xl",
          "animate-in slide-in-from-bottom duration-200",
          // max-h + flex pozwala przewijać tylko sekcję children gdy form
          // jest dluzszy niz ekran. Bez tego sheet rosnie poza viewport
          // a top (np. label "Imię") chowa się ponad ekranem.
          "flex flex-col max-h-[92dvh]",
          className,
        )}
      >
        <div className="flex justify-center pt-2 pb-1 shrink-0">
          <div className="h-1 w-10 rounded-full bg-line" />
        </div>
        {title ? (
          <div className="flex items-center justify-between px-5 pb-2 pt-1 shrink-0">
            <h2 className="text-base font-semibold tracking-tight">{title}</h2>
            <button onClick={onClose} className="p-1 -mr-1 text-muted hover:text-ink" aria-label="Zamknij">
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : null}
        <div className="px-5 pb-5 overflow-y-auto flex-1 min-h-0">{children}</div>
        {footer ? <div className="px-5 pb-[max(20px,env(safe-area-inset-bottom))] pt-2 border-t border-line shrink-0">{footer}</div> : null}
      </div>
    </div>
  );
}
